import { FC, useState } from "react";
import { Modal, Text, DefaultButton } from "@fluentui/react";
import "./SingleDownload.css";
import { downloadDocuments } from "../../../utils/download";
import CrossIcon from "../../../assets/Icons/svg/crossIcon";
import Popup from "../../Popup/Popup";
import ErrorIcon from "../../../assets/Icons/svg/errorIcon";
import SuccessIcon from "../../../assets/Icons/svg/successIcon";
import { Document } from "../../../models";
import DownloadIcon from "../../../assets/Icons/svg/downloadIcon";

interface DownloadPopupProps {
  isModalOpen: boolean;
  hideModal: () => void;
  showModal: () => void;
  documents: Document[];
}

const SingleDownload: FC<DownloadPopupProps> = ({
  isModalOpen,
  hideModal,
  showModal,
  documents,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  
  const modalStyles = {
    root: {
      background: "rgba(0, 0, 0, 0.5)",
    },
    main: {
      borderRadius: "12px",
      backgroundColor: "#ECF0F9",
      width: "auto",
      minHeight: "200px",
    },
  };

  const onDismiss = (ev?: React.SyntheticEvent<HTMLElement>) => {
    // Check if the event is a keyboard event and if ESC was pressed
    const keyboardEvent = ev as KeyboardEvent | undefined;
    if (keyboardEvent?.type === 'keydown' && keyboardEvent?.code === 'Escape') {
      keyboardEvent.preventDefault();
      keyboardEvent.stopPropagation();
      return;
    }

    hideModal();
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      if (documents) {
        await downloadDocuments({
          // DEX-559: email-link downloads feed DocumentDto objects that only carry `id`
          // (no `documentId`), so read `id` and drop nulls — matches MultipleDownload.
          documentIds: documents
            .map((doc: Document) => doc.id)
            .filter((id): id is number => id != null),
          onSuccess: () => {
            setShowSuccessPopup(true);
            hideModal();
          },
          onError: (error) => {
            setShowErrorPopup(true);
          },
          onLoadingChange: (loading) => {
            setIsDownloading(loading);
          }
        });
      } else {
        console.error("SingleDownload - Missing required data:", { documents });
      }
      hideModal();
      
    } catch (error) {
      console.error("Error during download:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        onDismiss={onDismiss}
        styles={modalStyles}
        className="download-popup-modal"
      >
        <div className="downloadPopupContainer">
            <div className="downloadContainer">
              <Text className="dowloadModalHeading">Download Document</Text>
              <div className="closeIcon" onClick={hideModal}>
                <CrossIcon width="10px" height="10px" />
              </div>
            </div>
            <div className="dowloadModalBody">
              <div className="downloadPopupIcon">
                <DownloadIcon />
              </div>
              
              <Text className="dowloadModalBodyTextContainer">
                <p className="dowloadModalBodyTitle">Are you sure you want to download this document?</p>
                <p className="dowloadModalBodyText">Changes made to the document offline will not be saved in Dexterity.</p>
              </Text>
              <div className="downloadButtonsContainer">
                <div style={{ display: "flex", flexDirection: "row", gap: "2vh" }}>
                  <DefaultButton 
                    onClick={handleDownload} 
                    className="downloadButton"
                    disabled={isDownloading}
                  >
                    {isDownloading ? "Downloading..." : "Download"}
                  </DefaultButton>
                  <DefaultButton
                    onClick={hideModal}
                    className="downloadCancelButton"
                  >
                    Cancel
                  </DefaultButton>
                </div>
              </div>
            </div>
          </div>
      </Modal>

      {/* Success Popup */}
      
          <Popup 
            isOpen={showSuccessPopup} 
            title="Download Completed" 
            iconName={<SuccessIcon />}
            contentTitle="" 
            content="The document has been downloaded successfully." 
            buttons={[]} 
            customContent=""
            onClose={() => {
              setShowSuccessPopup(false);
            }}
          />

      {/* Error Popup */}
      <Popup 
        isOpen={showErrorPopup} 
        title="Download Failed" 
        iconName={<ErrorIcon />} 
        contentTitle="Download Failed" 
        content={`The document has not been downloaded. Please try again.`} 
        buttons={[]} 
        customContent=""
        onClose={() => {
          setShowErrorPopup(false);
        }}
      />
    </>
  );
};

export default SingleDownload;
